import { useMemo, type KeyboardEvent, type MouseEvent } from "react";
import { classNames } from "../../ui/styles/classNames";

/** Words that support something said about the text, such as a statement; offsets as the spans' (O-1). */
export interface EvidenceMark {
  /** What the words support. */
  readonly key: string;
  readonly start: number;
  readonly end: number;
  /** What its words are labelled with, drawn as the annotations' bar under them, such as "#3 Litigation". */
  readonly label?: string;
}

/**
 * Which evidence shows highlighted, and how pointing at or choosing it is followed (O-1): the
 * same on the text's words and on what they support, so either side leads to the other.
 */
export interface EvidenceFocus {
  /** The keys whose words, and what they support, show highlighted: those pointed at, else those chosen. */
  readonly highlighted: ReadonlySet<string>;
  /** The keys chosen, which stay highlighted until chosen again or let go. */
  readonly selected: ReadonlySet<string>;
  /** Pointing at it previews; `null` once the pointer leaves. */
  readonly onPoint: (keys: readonly string[] | null) => void;
  /** Clicking it chooses. */
  readonly onSelect: (keys: readonly string[]) => void;
}

/** The evidence on the text, followed from its words to what they support. */
export interface EvidenceLayer extends EvidenceFocus {
  readonly marks: readonly EvidenceMark[];
}

/** What the element drawing a piece of the text takes to show and follow the evidence there. */
export interface EvidenceSegmentProps {
  readonly "data-evidence-at"?: number;
  readonly className?: string;
  /** Where a piece of evidence starts: one keyboard stop for it, which previews on focus and chooses on Enter or Space. */
  readonly tabIndex?: number;
  readonly role?: "button";
  readonly "aria-pressed"?: boolean;
  readonly onKeyDown?: (event: KeyboardEvent) => void;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
}

/** How a text view draws its evidence and follows it (`useTextEvidence`). */
export interface TextEvidence {
  /** Offsets the text splits at, so each piece of evidence starts and ends a drawn segment. */
  readonly boundaries: readonly number[];
  /** For the element that draws the text from `start`: marked as evidence, highlighted while its keys are. */
  readonly segmentProps: (start: number) => EvidenceSegmentProps;
  /** For the element around the text: pointing at evidence previews what it supports. */
  readonly followProps: { readonly onMouseOver?: (event: MouseEvent) => void; readonly onMouseLeave?: () => void };
  /** A click on `target` chooses the evidence there, if it is evidence. */
  readonly select: (target: EventTarget) => void;
}

const NO_MARKS: readonly EvidenceMark[] = [];

/** What the words at an offset support, once each, in the order of the marks. */
export function evidenceKeysAt(marks: readonly EvidenceMark[], offset: number): string[] {
  return [...new Set(marks.filter((mark) => mark.start <= offset && offset < mark.end).map((mark) => mark.key))];
}

/** The evidence a text view draws, in the view's own offsets; `undefined` draws none. */
export function useTextEvidence(evidence: EvidenceLayer | undefined): TextEvidence {
  const marks = evidence?.marks ?? NO_MARKS;
  const boundaries = useMemo(() => marks.flatMap((mark) => [mark.start, mark.end]), [marks]);
  const under = (target: EventTarget) => {
    const at = target instanceof Element ? target.closest("[data-evidence-at]")?.getAttribute("data-evidence-at") : null;
    return at == null ? null : evidenceKeysAt(marks, Number(at));
  };
  return {
    boundaries,
    segmentProps: (start) => {
      const keys = evidenceKeysAt(marks, start);
      if (keys.length === 0 || evidence === undefined) return {};
      const highlighted = keys.some((key) => evidence.highlighted.has(key));
      const drawn = { "data-evidence-at": start, className: classNames("app-evidence cursor-pointer rounded-sm", highlighted && "app-evidence-highlighted") };
      // A piece of evidence split by spans is one stop for the keyboard, at its start.
      if (!marks.some((mark) => mark.start === start)) return drawn;
      return {
        ...drawn,
        tabIndex: 0,
        role: "button",
        "aria-pressed": keys.some((key) => evidence.selected.has(key)),
        onKeyDown: (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          evidence.onSelect(keys);
        },
        onFocus: () => { evidence.onPoint(keys); },
        onBlur: () => { evidence.onPoint(null); },
      };
    },
    followProps: evidence === undefined ? {} : {
      onMouseOver: (event) => { evidence.onPoint(under(event.target)); },
      onMouseLeave: () => { evidence.onPoint(null); },
    },
    select: (target) => {
      const keys = under(target);
      if (keys !== null) evidence?.onSelect(keys);
    },
  };
}
